/**
 * Exercise, Benchmark, Progression & Multi-Sport API client.
 * Consumes endpoints from Phases 2-4 of the multi-sport coach system.
 */

import { exercisesApi } from "@/lib/api/typedClient";
import { api } from "./api";
import { log } from "./logger";

// ── Exercise Types ──────────────────────────────────────────────────────────

export interface ExerciseLogCreate {
  exercise_name: string;
  set_number?: number;
  reps?: number | null;
  weight_kg?: number | null;
  rpe?: number | null;
  tempo?: string | null;
  duration_seconds?: number | null;
  distance_meters?: number | null;
  notes?: string | null;
}

export interface ExerciseLogBatchCreate {
  activity_id?: string | null;
  sets: ExerciseLogCreate[];
}

export interface ExerciseLogResponse {
  id: string;
  activity_id: string | null;
  user_id: string;
  exercise_definition_id: string;
  exercise_name: string;
  set_number: number;
  reps: number | null;
  weight_kg: number | null;
  rpe: number | null;
  tempo: string | null;
  duration_seconds: number | null;
  distance_meters: number | null;
  notes: string | null;
  created_at: string;
}

export interface ExerciseHistoryResponse {
  exercise_name: string;
  logs: ExerciseLogResponse[];
  total: number;
}

// ── Benchmark Types ─────────────────────────────────────────────────────────

export interface BenchmarkCreate {
  benchmark_type: string;
  benchmark_name: string;
  value: number;
  unit: string;
  source?: string;
  activity_id?: string | null;
  notes?: string | null;
}

export interface BenchmarkResponse {
  id: string;
  user_id: string;
  benchmark_type: string;
  benchmark_name: string;
  value: number;
  unit: string;
  recorded_at: string;
  source: string;
  activity_id: string | null;
  notes: string | null;
  is_pr?: boolean;
}

export interface BenchmarkHistoryResponse {
  benchmark_name: string;
  history: BenchmarkResponse[];
  total: number;
}

export interface BenchmarkLatestResponse {
  benchmarks: BenchmarkResponse[];
}

// ── Progression Types ───────────────────────────────────────────────────────

export interface ProgressionResponse {
  sport: string;
  status: string;
  data: Record<string, unknown>;
}

export interface PRRecord {
  benchmark_name: string;
  value: number;
  unit: string;
  benchmark_type: string;
  recorded_at: string;
  sport: string | null;
}

export interface RecentPRsResponse {
  prs: PRRecord[];
  total: number;
}

// ── Exercise API ────────────────────────────────────────────────────────────

export async function logExercises(
  payload: ExerciseLogBatchCreate,
): Promise<ExerciseLogResponse[]> {
  log.debug("API", "Logging exercises");
  try {
    const response = await exercisesApi.log(payload);
    return response.data as ExerciseLogResponse[];
  } catch (error) {
    log.error("API", "Failed to log exercises:", error);
    throw error;
  }
}

export async function fetchExerciseHistory(
  exerciseName: string,
  limit = 50,
  offset = 0,
): Promise<ExerciseHistoryResponse> {
  log.debug("API", "Fetching exercise history", exerciseName);
  try {
    const response = await api.get(
      `/api/exercises/history/${encodeURIComponent(exerciseName)}`,
      { params: { limit, offset } },
    );
    return response.data as ExerciseHistoryResponse;
  } catch (error) {
    log.error("API", "Failed to fetch exercise history:", error);
    throw error;
  }
}

// ── Benchmark API ───────────────────────────────────────────────────────────

export async function recordBenchmark(
  payload: BenchmarkCreate,
): Promise<BenchmarkResponse> {
  log.debug("API", "Recording benchmark");
  try {
    const response = await exercisesApi.recordBenchmark(payload);
    return response.data as BenchmarkResponse;
  } catch (error) {
    log.error("API", "Failed to record benchmark:", error);
    throw error;
  }
}

export async function fetchBenchmarkHistory(
  benchmarkName: string,
  limit = 50,
  offset = 0,
): Promise<BenchmarkHistoryResponse> {
  log.debug("API", "Fetching benchmark history", benchmarkName);
  try {
    const response = await api.get(
      `/api/benchmarks/${encodeURIComponent(benchmarkName)}/history`,
      { params: { limit, offset } },
    );
    return response.data as BenchmarkHistoryResponse;
  } catch (error) {
    log.error("API", "Failed to fetch benchmark history:", error);
    throw error;
  }
}

export async function fetchLatestBenchmarks(): Promise<BenchmarkLatestResponse> {
  log.debug("API", "Fetching latest benchmarks");
  try {
    const response = await exercisesApi.getLatestBenchmarks();
    return response.data as BenchmarkLatestResponse;
  } catch (error) {
    log.error("API", "Failed to fetch latest benchmarks:", error);
    throw error;
  }
}

// ── Progression API ─────────────────────────────────────────────────────────

export async function fetchProgression(
  sport: string,
): Promise<ProgressionResponse> {
  log.debug("API", "Fetching progression", sport);
  try {
    const response = await api.get(
      `/analytics/progression/${encodeURIComponent(sport)}`,
    );
    return response.data as ProgressionResponse;
  } catch (error) {
    log.error("API", "Failed to fetch progression:", error);
    throw error;
  }
}

export async function fetchRecentPRs(
  limit = 20,
): Promise<RecentPRsResponse> {
  log.debug("API", "Fetching recent PRs");
  try {
    const response = await exercisesApi.getRecentPRs({ limit });
    return response.data as RecentPRsResponse;
  } catch (error) {
    log.error("API", "Failed to fetch recent PRs:", error);
    throw error;
  }
}
